import React, {Fragment, useMemo, useState} from "react";
import {ArrowDropDown, Build} from "@mui/icons-material";
import {IconButton, ListSubheader, Menu, MenuItem, Tooltip} from "@mui/material";
import Button from "@mui/material/Button";
import {useTranslation} from "react-i18next";
import {useDispatch} from "react-redux";
import {supersetSynchronize} from "../../state/miscellaneous/miscellaneousActions";
import {
  showAgentList,
  showEasyLoadingWmsModal,
  showEtlWizardModal,
  showPackageLicenseList,
  showResourceLicenseList,
  showThemeList
} from "../../state/package-manager/packageManagerActions";
import EXTRAS from "../../utils/extras";
import {HEADER_BUTTON_VIEW_MODE_ICON, HEADER_BUTTON_VIEW_MODE_ICONTEXT} from "../header/utils";

const ToolsSelect = ({
  node,
  nodeId,
  viewMode,
  isEasyLoadingVisible,
  isTaxonomiesManagerVisible,
  isDashboardManagerAvailable,
  isMetadataManagerAvailable,
  isStatsManagerAvailable,
  onNavigate
}) => {
  const {t} = useTranslation();
  const dispatch = useDispatch();

  const [anchorEl, setanchorEl] = useState(null);
  const handleOpen = event => {
    setanchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setanchorEl(null);
  };

  const nodeExtras = useMemo(() => EXTRAS.parse(node?.extras), [node]);
  const {SupersetBaseUrl, CkanBaseUrl, StatsManagerBaseUrl} = nodeExtras;

  return (
    <Fragment>
      <Tooltip title={t("components.toolsSelect.title")}>
        {viewMode === HEADER_BUTTON_VIEW_MODE_ICON ? (
          <IconButton onClick={handleOpen} aria-label={t("components.toolsSelect.ariaLabel")} color="inherit">
            <Build />
          </IconButton>
        ) : (
          <Button
            onClick={handleOpen}
            startIcon={viewMode === HEADER_BUTTON_VIEW_MODE_ICONTEXT ? <Build /> : null}
            endIcon={<ArrowDropDown />}
            aria-label={t("components.toolsSelect.ariaLabel")}
            classes={{
              endIcon: "button-select__menu-button__end-icon"
            }}
            sx={{
              background: Boolean(anchorEl) ? "#f5f5f5" : undefined
            }}
          >
            {t("components.toolsSelect.label")}
          </Button>
        )}
      </Tooltip>

      <Menu
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        anchorOrigin={{
          vertical: "top",
          horizontal: "left"
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right"
        }}
        onClose={handleClose}
        sx={{
          "& .MuiMenuItem-root": {
            paddingLeft: "32px"
          }
        }}
        keepMounted
      >
        {isEasyLoadingVisible ? (
          <ListSubheader>{t("components.toolsSelect.menuSections.easyLoading")}</ListSubheader>
        ) : null}
        {isEasyLoadingVisible ? (
          <MenuItem
            key="easyDataLoading"
            id="tools-select__easy-data-loading-btn"
            onClick={() => {
              handleClose();
              dispatch(showEtlWizardModal(null, false, node.nodeId, node.code));
            }}
          >
            {t("components.toolsSelect.menuItems.easyDataLoading")}
          </MenuItem>
        ) : null}
        {isEasyLoadingVisible ? (
          <MenuItem
            key="easyWmsLoading"
            id="tools-select__easy-wms-loading-btn"
            onClick={() => {
              handleClose();
              dispatch(showEasyLoadingWmsModal());
            }}
          >
            {t("components.toolsSelect.menuItems.easyWmsLoading")}
          </MenuItem>
        ) : null}
        {isTaxonomiesManagerVisible ? (
          <ListSubheader>{t("components.toolsSelect.menuSections.taxonomiesManager")}</ListSubheader>
        ) : null}
        {isTaxonomiesManagerVisible ? (
          <MenuItem
            key="agentsManager"
            id="tools-select__agents-manager-btn"
            onClick={() => {
              handleClose();
              dispatch(showAgentList());
            }}
          >
            {t("components.toolsSelect.menuItems.agentsManager")}
          </MenuItem>
        ) : null}
        {isTaxonomiesManagerVisible ? (
          <MenuItem
            key="themesManager"
            id="tools-select__themes-manager-btn"
            onClick={() => {
              handleClose();
              dispatch(showThemeList());
            }}
          >
            {t("components.toolsSelect.menuItems.themesManager")}
          </MenuItem>
        ) : null}
        {isTaxonomiesManagerVisible ? (
          <MenuItem
            key="packageLicensesManager"
            id="tools-select__package-licenses-manager-btn"
            onClick={() => {
              handleClose();
              dispatch(showPackageLicenseList());
            }}
          >
            {t("components.toolsSelect.menuItems.packageLicensesManager")}
          </MenuItem>
        ) : null}
        {isTaxonomiesManagerVisible ? (
          <MenuItem
            key="resourceLicensesManager"
            id="tools-select__resource-licenses-manager-btn"
            onClick={() => {
              handleClose();
              dispatch(showResourceLicenseList());
            }}
          >
            {t("components.toolsSelect.menuItems.resourceLicensesManager")}
          </MenuItem>
        ) : null}
        {isMetadataManagerAvailable ? (
          <ListSubheader>{t("components.toolsSelect.menuSections.metadataManager")}</ListSubheader>
        ) : null}
        {isMetadataManagerAvailable ? (
          <MenuItem
            key="metadataManager"
            id="tools-select__metadata-manager-btn"
            onClick={() => {
              handleClose();
              if (onNavigate) onNavigate();
              window.open(CkanBaseUrl, "_blank");
            }}
          >
            {t("components.toolsSelect.menuItems.metadataManager")}
          </MenuItem>
        ) : null}
        {isDashboardManagerAvailable ? (
          <ListSubheader>{t("components.toolsSelect.menuSections.dashboardManager")}</ListSubheader>
        ) : null}
        {isDashboardManagerAvailable && SupersetBaseUrl ? (
          <MenuItem
            key="dashboardManager"
            id="tools-select__dashboard-manager-btn"
            onClick={() => {
              handleClose();
              if (onNavigate) onNavigate();
              window.open(SupersetBaseUrl, "_blank");
            }}
          >
            {t("components.toolsSelect.menuItems.dashboardManager")}
          </MenuItem>
        ) : null}
        {isDashboardManagerAvailable ? (
          <MenuItem
            key="dashboardManagerSync"
            id="tools-select__dashboard-manager-sync-btn"
            onClick={() => {
              handleClose();
              dispatch(supersetSynchronize(nodeId));
            }}
          >
            {t("components.toolsSelect.menuItems.dashboardManagerSync")}
          </MenuItem>
        ) : null}
        {isStatsManagerAvailable ? (
          <ListSubheader>{t("components.toolsSelect.menuSections.statsManager")}</ListSubheader>
        ) : null}
        {isStatsManagerAvailable ? (
          <MenuItem
            key="statsManager"
            id="tools-select__stats-manager-btn"
            onClick={() => {
              handleClose();
              if (onNavigate) onNavigate();
              window.open(StatsManagerBaseUrl, "_blank");
            }}
          >
            {t("components.toolsSelect.menuItems.statsManager")}
          </MenuItem>
        ) : null}
      </Menu>
    </Fragment>
  );
};

export default ToolsSelect;
